/* purchase-info.js
 *
 * Returning-user purchase signal — STEP 1 of the returning-user urgency flow.
 *
 * On a returning visitor (an existing visitorID in localStorage), fetch their
 * purchase summary from /api/visitor/orders, CACHE it, and expose it as
 * `window.SE_purchaseInfo` plus a `SE_purchaseInfoReady` event. The flow gate
 * (step 2) reads this to choose new-user vs returning-user behaviour.
 *
 * Read-only and fail-safe: if there's no usable UID or the backend is
 * unreachable, it publishes {known:false, has_purchased:false} so the gate
 * cleanly falls through to the new-user flow. Never blocks the page.
 *
 * Backend seam: window.AGB_VISITOR_ORDERS_URL (per store; defaults to the
 * ROW-US data backend). EU/IL must override — see /agilite-pilot-port §4.
 */
(function () {
  'use strict';

  var ENDPOINT = window.AGB_VISITOR_ORDERS_URL ||
    'https://database-app-1.onrender.com/api/visitor/orders';  // moved off -2-lcae (load rebalance; no geoip needed)
  var CACHE_KEY = 'SE_purchaseInfo';
  var TTL_MS = window.SE_PURCHASE_INFO_TTL_MS || (6 * 60 * 60 * 1000); // 6h
  var UUID_RE = /^[a-fA-F0-9-]{32,40}$/;
  var started = false;

  // Prefer the engine-resolved identity (post /identify, which may have adopted
  // a server visitor_id); fall back to the raw localStorage UID.
  function readVisitorId() {
    try {
      if (window.SE_visitorIdentity && window.SE_visitorIdentity.visitor_id) {
        return window.SE_visitorIdentity.visitor_id;
      }
      return localStorage.getItem('viewerId') ||
             localStorage.getItem('visitorID') || null;
    } catch (e) { return null; }
  }

  function publish(info, source) {
    info = info || { known: false, has_purchased: false };
    window.SE_purchaseInfo = info;
    try {
      window.dispatchEvent(new CustomEvent('SE_purchaseInfoReady', {
        detail: { info: info, source: source }
      }));
    } catch (e) {}
  }

  function loadCache(vid) {
    try {
      var c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!c || c.visitorID !== vid) return null;               // different/no UID
      if (!c.fetchedAt || (Date.now() - c.fetchedAt) > TTL_MS) return null; // stale
      return c.info || null;
    } catch (e) { return null; }
  }

  function saveCache(vid, info) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        visitorID: vid, fetchedAt: Date.now(), info: info
      }));
    } catch (e) {}
  }

  function start() {
    if (started) return;
    started = true;

    var vid = readVisitorId();
    if (!vid || !UUID_RE.test(vid)) {
      // Truly new, or junk UID ('Your text here' / '1' / …) -> new-user flow.
      publish({ known: false, has_purchased: false, _reason: 'no-visitorID' }, 'none');
      return;
    }

    var cached = loadCache(vid);
    if (cached) { publish(cached, 'cache'); return; }          // reuse across navigation

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorID: vid }),
      credentials: 'omit',
      keepalive: true
    })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (info) {
        if (info && !info.error) {
          saveCache(vid, info);
          publish(info, 'network');
        } else {
          publish({ known: false, has_purchased: false, _reason: 'bad-response' }, 'error');
        }
      })
      .catch(function () {
        // Backend down / blocked -> behave as new. No page impact.
        publish({ known: false, has_purchased: false, _reason: 'fetch-error' }, 'error');
      });
  }

  // Existing UID (returning visitor) -> fetch immediately. Otherwise wait for
  // the engine to resolve/adopt one (SE_visitorIdentified), with a short poll
  // fallback so we never hang if that event doesn't fire.
  if (readVisitorId()) {
    start();
  } else {
    window.addEventListener('SE_visitorIdentified', start);
    var tries = 0;
    var iv = setInterval(function () {
      if (readVisitorId() || ++tries > 50) {                   // ~10s cap
        clearInterval(iv);
        start();
      }
    }, 200);
  }
})();
